import React from 'react';
import {
  Heading,
  SimpleGrid,
  VStack,
  Text,
  Icon,
  Box,
  HStack,
} from '@chakra-ui/react';
import { FaCalendarAlt, FaUsers, FaStar, FaInfoCircle } from 'react-icons/fa';
import {
  CIL,
  SECTION_TITLE_CONTAINER_BG,
  SECTION_TITLE_TEXT_COLOR,
} from './constants';

/** Card tariffa: sfondo bianco per leggibilità, bordo e icona colorati */
const PriceCard = ({ title, price, description, highlightColor, icon }) => (
  <Box
    bg="white"
    p={{ base: 6, md: 8 }}
    borderRadius="xl"
    borderWidth="1px"
    borderColor="gray.200"
    borderLeftWidth="4px"
    borderLeftColor={highlightColor}
    textAlign="center"
    boxShadow="0 4px 20px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(0, 0, 0, 0.06)"
  >
    <VStack spacing={3} align="stretch">
      <Icon as={icon} w={10} h={10} color={highlightColor} mx="auto" />
      <Text fontWeight="bold" fontSize="md" color="gray.800">
        {title}
      </Text>
      <Text
        fontWeight="bold"
        fontSize={{ base: '3xl', md: '4xl' }}
        color={CIL.plum}
      >
        {price}€
      </Text>
      <Text fontSize="sm" color="gray.700">
        {description}
      </Text>
    </VStack>
  </Box>
);

const PricingSection = ({ event }) => {
  const prezzoSettimana =
    event?.Varianti?.[0]?.prezzoVariante ?? event?.prezzo ?? 120;
  const scontoFratelli = event?.Sconto_Fratelli ?? 20;
  const prezzoFratelli = prezzoSettimana - (Number(scontoFratelli) || 0);
  const prezzoMVP =
    event?.Sconto_MVP != null && event?.Sconto_MVP !== ''
      ? Number(event.Sconto_MVP)
      : 700;

  // Acconto e saldo (per quota settimanale): da event o fallback 50% del prezzo
  const accontoAmount =
    event?.accontoAmount != null && event.accontoAmount !== ''
      ? Number(event.accontoAmount)
      : Math.floor(Number(prezzoSettimana) * 0.5);
  const saldoAmount = Number(prezzoSettimana) - accontoAmount;

  const priceOptions = [
    {
      id: 'settimanale',
      label: 'Quota Settimanale',
      price: prezzoSettimana,
      description: 'Per ogni settimana scelta',
      highlightColor: CIL.sky,
      icon: FaCalendarAlt,
    },
    {
      id: 'fratelli',
      label: 'Sconto Fratelli',
      price: prezzoFratelli,
      description: 'Valido per secondo figlio e successivi',
      highlightColor: CIL.yellow,
      icon: FaUsers,
    },
    {
      id: 'mvp',
      label: 'Pacchetto MVP',
      price: prezzoMVP,
      description: 'Tutte le settimane',
      highlightColor: CIL.teal,
      icon: FaStar,
    },
  ];

  return (
    <Box
      as="section"
      id="section-quote"
      py={{ base: 10, md: 20 }}
      scrollMarginTop={{ base: '80px', md: '140px' }}
    >
      <Box w="100%" maxW="100%" px={{ base: 4, md: 6, lg: 8 }}>
        <Heading textAlign="center" mb={10} size="lg">
          <Box
            w="100%"
            px={4}
            py={3}
            borderRadius="lg"
            sx={{ background: SECTION_TITLE_CONTAINER_BG }}
            boxShadow="sm"
          >
            <Text as="span" fontWeight="bold" color={SECTION_TITLE_TEXT_COLOR}>
              Quote 2026
            </Text>
          </Box>
        </Heading>
        <SimpleGrid
          columns={{ base: 1, md: 3 }}
          spacing={8}
          w="100%"
          maxW="100%"
        >
          {priceOptions.map((opt) => (
            <PriceCard
              key={opt.id}
              title={opt.label}
              price={opt.price}
              description={opt.description}
              highlightColor={opt.highlightColor}
              icon={opt.icon}
            />
          ))}
        </SimpleGrid>

        {/* Acconto e saldo: come funzionano + importi calcolati */}
        <Box
          w="100%"
          maxW="100%"
          mt={10}
          p={{ base: 5, md: 6 }}
          bg="white"
          borderRadius="xl"
          borderWidth="1px"
          borderColor="gray.200"
          borderLeftWidth="4px"
          borderLeftColor={CIL.plum}
          boxShadow="0 4px 20px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(0, 0, 0, 0.06)"
        >
          <HStack spacing={2} mb={3}>
            <Icon as={FaInfoCircle} color={CIL.plum} w={5} h={5} />
            <Text fontWeight="bold" fontSize="md" color="gray.800">
              Acconto e saldo
            </Text>
          </HStack>
          <Text fontSize="sm" color="gray.700" mb={4} lineHeight="tall">
            Puoi pagare in un&apos;unica soluzione (totale) oppure in due tempi:{' '}
            <strong>acconto</strong> per confermare la prenotazione e{' '}
            <strong>saldo</strong> entro il venerdì precedente la settimana.
            L&apos;acconto non è rimborsabile.
          </Text>
          <HStack
            spacing={{ base: 4, md: 8 }}
            flexWrap="wrap"
            fontSize="sm"
            color="gray.800"
            bg="gray.50"
            p={4}
            borderRadius="lg"
          >
            <Box>
              <Text color="gray.600" fontWeight="600" mb={0.5}>
                Esempio per una settimana (quota {prezzoSettimana}€)
              </Text>
              <Text>
                Acconto: <strong>{accontoAmount}€</strong> · Saldo:{' '}
                <strong>{saldoAmount}€</strong>
              </Text>
            </Box>
          </HStack>
        </Box>
      </Box>
    </Box>
  );
};

export default PricingSection;
